import { Component } from 'react';

import PoiCard from '../PoiCard';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type {
  PoiData,
  PoiDataArray,
} from '@shared/types/frontend/nearby-transport-response';
import type { Coordinates } from '@shared/types/google-maps';
import type { EntityId, Nullable } from '@shared/types/utils';

import STYLES from './PanelBody.module.scss';

import EndTip from '@hotels-hotel-detail/ui/EndTip';
import {
  THING_TO_DO,
  TRANSPORT,
  MAPPING_MAP_CLICK_TYPE,
} from '@hotels-hotel-detail/utils/location-constants';
import { buildAdditionalInfoNearbyMap } from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  disableBodyScroll,
  enableBodyScroll,
} from '@shared/utils-frontend/body-scroll-lock';
import {
  ACTION_TYPE,
  CLICK_TYPE,
} from '@shared/utils-frontend/mini-events/constants';

type Props = {
  nearbyPoi?: PoiDataArray;
  airport?: PoiDataArray;
  transportWithoutAirport?: PoiDataArray;
  selectedTab: string;
  listContainerRefCb: (el: HTMLDivElement) => void;
  cardRefCb: (el: HTMLDivElement, id: EntityId) => void;
  onPoiMarkerClick: (
    entityId: Nullable<EntityId>,
    selectedCoordinates: Nullable<Coordinates>,
  ) => void;
  selectedEntityId: Nullable<EntityId>;
  hotelID: string;
  miniEventTracker: MiniEventTracker;
};

class PanelBody extends Component<Props> {
  listContainer: HTMLDivElement | undefined;

  static defaultProps = {
    cardRefCb: () => {},
  };

  componentDidMount() {
    if (this.listContainer) {
      disableBodyScroll(this.listContainer);
    }
  }

  componentWillUnmount() {
    if (this.listContainer) {
      enableBodyScroll(this.listContainer);
    }
  }

  getPoiCard(poiDatas?: PoiDataArray) {
    const { cardRefCb, onPoiMarkerClick, selectedEntityId, selectedTab } =
      this.props;
    return poiDatas?.map((poiData, index) => {
      const { entityId, latitude, linearDistance, longitude } = poiData;
      const isSelected = entityId === selectedEntityId;

      return (
        <PoiCard
          key={entityId}
          poiData={poiData}
          isSelected={isSelected}
          isLast={index === poiDatas.length - 1}
          refCallback={(ref: HTMLDivElement) => cardRefCb(ref, entityId)}
          onClick={() => {
            onPoiMarkerClick(entityId, { latitude, longitude });
            this.trackClick(selectedTab, { entityId, linearDistance });
          }}
        />
      );
    });
  }

  trackClick = (
    mapClickType: Nullable<string>,
    selectedPoi:
      | PoiData
      | {
          entityId: EntityId;
          linearDistance: Nullable<number>;
        },
  ) => {
    const { hotelID, miniEventTracker } = this.props;

    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.DETAIL_PAGE_NEARBY_MAP_CLICK,
      buildAdditionalInfoNearbyMap({
        isMapFullView: true,
        hotelId: parseInt(hotelID, 10),
        clickType:
          (mapClickType && CLICK_TYPE[MAPPING_MAP_CLICK_TYPE[mapClickType]]) ||
          '',
        poiInfo: selectedPoi
          ? {
              poi_id: parseInt(selectedPoi.entityId, 10),
              distance_in_meters: selectedPoi.linearDistance,
            }
          : null,
      }),
    );
  };

  render() {
    const {
      airport,
      listContainerRefCb,
      nearbyPoi,
      selectedTab,
      transportWithoutAirport,
    } = this.props;
    let cards;
    if (selectedTab === THING_TO_DO && nearbyPoi) {
      cards = this.getPoiCard(nearbyPoi);
    }
    if (selectedTab === TRANSPORT && transportWithoutAirport) {
      cards = this.getPoiCard(airport?.concat(transportWithoutAirport));
    }
    if (cards) {
      return (
        <div
          className={STYLES.PanelBody__listContainer}
          ref={(el: HTMLDivElement) => {
            this.listContainer = el;
            listContainerRefCb(el);
          }}
        >
          {cards}
          <EndTip className={STYLES.PanelBody__endtip} />
        </div>
      );
    }
    return null;
  }
}

export default withMiniEventTracker(PanelBody);
